import { getBackgroundStyleFromImageURL, getCloneId } from 'utils';
import {
  createCanvasWithGradient,
  createCanvasWithShape,
  transformShape
} from '../utils/GraphicsUtils';
import BackgroundStyle from './BackgroundStyle';
import {
  createMask,
  FrameData,
  PointData,
  ShapeData,
  ShapeFrameData,
  SimpleShapeData
} from './FlyerData';
import { Frame } from './Frame';
import { FrameType } from './FrameType';

const getShapeBoundingBox = (shape: ShapeData): PIXI.Rectangle => {
  if (Array.isArray(shape)) {
    let x: number,
      y: number,
      right: number,
      bottom: number,
      width: number,
      height: number;
    let p = shape[0];
    x = right = p.x;
    y = bottom = p.y;
    for (let i = 1; i < shape.length; i++) {
      p = shape[i];
      x = Math.min(x, p.x);
      y = Math.min(y, p.y);
      right = Math.max(right, p.x);
      bottom = Math.max(bottom, p.y);
    }
    width = right - x;
    height = bottom - y;
    return new PIXI.Rectangle(x, y, width, height);
  } else {
    return new PIXI.Rectangle(shape.x, shape.y, shape.width, shape.height);
  }
};

const transformHole = (
  prevBox: PIXI.Rectangle,
  newBox: PIXI.Rectangle,
  data: PointData[] | SimpleShapeData
): PointData[] | SimpleShapeData => {
  const left = prevBox.x;
  const top = prevBox.y;
  const dx = newBox.x - prevBox.x;
  const dy = newBox.y - prevBox.y;
  const sx = newBox.width / prevBox.width;
  const sy = newBox.height / prevBox.height;
  if (Array.isArray(data)) {
    // transform points
    return data.map(p => {
      // scale
      let x = left + (p.x - left) * sx;
      let y = top + (p.y - top) * sy;
      // move
      x += dx;
      y += dy;
      return { x, y };
    });
  } else {
    return {
      ...data,
      x: left + (data.x - left) * sx + dx,
      y: top + (data.y - top) * sy + dy,
      width: data.width * sx,
      height: data.height * sy
    };
  }
};

export class ShapeFrame extends Frame {
  type = FrameType.Shape;
  isEditable = true;
  isResizable = true;

  hole: ShapeData | undefined;

  get canRotate(): boolean {
    return true;
  }

  get rotation(): number {
    return this._rotation;
  }
  set rotation(value: number) {
    this._rotation = value;
    if (this.sprite) {
      this.sprite.angle = value;
    }
    if (this.sprite.mask) {
      this.sprite.mask.angle = value;
    }
  }

  private _mask: ShapeData;
  get mask(): ShapeData {
    return this._mask;
  }
  set mask(value: ShapeData) {
    this._mask = value;
    if (this.sprite) {
      const newBox = getShapeBoundingBox(value);
      const { x, y, width, height } = this._boundingBox;
      if (
        newBox.x !== x ||
        newBox.y !== y ||
        newBox.width !== width ||
        newBox.height !== height
      ) {
        this.sprite.x = newBox.x + newBox.width * 0.5;
        this.sprite.y = newBox.y + newBox.height * 0.5;
        this.sprite.width = newBox.width;
        this.sprite.height = newBox.height;
        this._boundingBox = newBox;
      }
      this.updateMask();
    }
    this.emit('update');
    this.generateThumbnail();
    this.emit('edited');
  }

  private _colors: string[] = [];
  get colors(): string[] {
    return this._colors;
  }
  set colors(value: string[]) {
    this._colors = value;
    if (this.sprite) {
      this.updateTexture();
    }
    this.emit('update');
    this.generateThumbnail();
    this.emit('edited');
  }

  // for template UI
  get color(): string {
    return this.colors[0];
  }
  set color(value: string) {
    this.colors = [value];
  }

  get boundingBox(): PIXI.Rectangle {
    return this._boundingBox;
  }
  set boundingBox(value: PIXI.Rectangle) {
    const prevBox = this._boundingBox;
    this._boundingBox = value;
    if (value) {
      if (!prevBox || prevBox.height !== value.height) {
        this.updateTexture();
      }
      if (this.sprite) {
        this.sprite.x = value.x + value.width * 0.5;
        this.sprite.y = value.y + value.height * 0.5;
        this.sprite.width = value.width;
        this.sprite.height = value.height;
      }
      if (prevBox) {
        this._mask = transformShape(prevBox, value, this.mask);
        if (this.hole) {
          this.hole = transformHole(prevBox, value, this.hole);
        }
        this.updateMask();
      }
    }
  }

  async load() {
    if (this.isLoaded) {
      return;
    }
    this.isLoaded = true;
    this.generateThumbnail();
    this.emit('load');
  }

  updateMask() {
    this.sprite.mask?.destroy(true);
    this.sprite.mask = createMask(this.mask, this.hole) as PIXI.Sprite;
    this.sprite.mask.anchor.set(0.5, 0.5);
    if (this.sprite.parent) {
      this.sprite.parent.addChild(this.sprite.mask);
    }
    this.sprite.mask.position.set(
      this.sprite.position.x,
      this.sprite.position.y
    );
    this.sprite.mask.angle = this._rotation;
  }

  updateTexture() {
    const oldTexture = this.sprite.texture;
    const canvas = createCanvasWithGradient(
      1,
      this.boundingBox.height,
      this.colors
    );
    this.sprite.texture = PIXI.Texture.from(canvas);
    oldTexture.destroy(true);
  }

  generateThumbnail(): void {
    if (this.thumbnailURL) {
      URL.revokeObjectURL(this.thumbnailURL);
    }
    const THUMBNAIL_SIZE: number = 120;
    const canvas = createCanvasWithGradient(
      THUMBNAIL_SIZE,
      THUMBNAIL_SIZE,
      this.colors
    );
    if (this.mask) {
      const maskCanvas = createCanvasWithShape(this.mask, this.hole);
      if (maskCanvas.width > 0 && maskCanvas.height > 0) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.globalCompositeOperation = 'destination-in';
          const scale =
            1 /
            Math.max(
              maskCanvas.width / THUMBNAIL_SIZE,
              maskCanvas.height / THUMBNAIL_SIZE
            );
          const dWidth = maskCanvas.width * scale;
          const dHeight = maskCanvas.height * scale;
          ctx.drawImage(
            maskCanvas,
            0,
            0,
            maskCanvas.width,
            maskCanvas.height,
            (canvas.width - dWidth) * 0.5,
            (canvas.height - dHeight) * 0.5,
            dWidth,
            dHeight
          );
        }
      }
    }
    this.thumbnailURL = canvas.toDataURL('image/png');
  }

  didUnselect(): void {
    this.generateThumbnail();
    getBackgroundStyleFromImageURL(this.thumbnailURL).then(
      (style: BackgroundStyle) => {
        this.backgroundStyle = style;
        this.emit('edited');
      }
    );
  }

  populateWithData(data: FrameData) {
    const shapeData = data as ShapeFrameData;
    this.boundingBox = getShapeBoundingBox(shapeData.mask);
    this._mask = shapeData.mask;
    this.hole = shapeData.hole;
    this.colors = shapeData.colors;
    this.sprite.mask = createMask(shapeData.mask, shapeData.hole);
    this.sprite.mask.anchor.set(0.5, 0.5);
    this.sprite.mask.position.set(
      this.sprite.position.x,
      this.sprite.position.y
    );
    if (shapeData.rotation) {
      this.rotation = shapeData.rotation;
    }
  }

  asData(forDatabase: boolean): FrameData {
    const { colors, mask } = this;
    const result: ShapeFrameData = {
      ...super.asData(forDatabase),
      colors,
      mask
    };
    if (this.hole) {
      result.hole = this.hole;
    }
    return result;
  }

  dispose() {
    if (this.sprite) {
      this.sprite.mask?.destroy(true);
      this.sprite.destroy(true);
    }
  }

  clone(copyIndex: number): Frame {
    const clone = new ShapeFrame(this.stageWidth, this.stageHeight);
    clone.id = getCloneId(this.id, copyIndex);
    clone.populateWithData(this.asData(false));
    clone.backgroundStyle = this.backgroundStyle;
    return clone;
  }
}



// WEBPACK FOOTER //
// ./src/data/ShapeFrame.ts